import { useState } from "react";
import { PasswordGate } from "./PasswordGate";
import {
  valuationSummary, currentSituation, marketData, revenueModel,
  projections, valuationMethods, berkusBreakdown, valuationScenarios,
  capTable, riskMatrix, milestones, productModules, teamInfo,
  tractionSignals
} from "../data/investorData";
import { images } from "../data/mock";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  TrendingUp, DollarSign, BarChart3, Users, Target,
  Shield, Clock, ChevronRight, ArrowLeft, Layers,
  Globe, AlertTriangle, CheckCircle2, Briefcase, ExternalLink
} from "lucide-react";

export default function InvestorsPage() {
  const [authenticated, setAuthenticated] = useState(
    sessionStorage.getItem("investor_auth") === "true"
  );

  if (!authenticated) {
    return <PasswordGate onSuccess={() => setAuthenticated(true)} />;
  }

  return (
    <div className="min-h-screen bg-brand-950 text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-brand-950/95 backdrop-blur-xl border-b border-brand-light/8">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="text-white/40 hover:text-white/70 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </a>
            <img src={images.logoWhite} alt="Tu Iglesia App" className="h-10 w-auto opacity-70" />
            <Separator orientation="vertical" className="h-6 bg-white/10" />
            <span className="brand-heading text-sm font-bold text-brand-light/70 uppercase tracking-widest">
              Reporte de Valoración
            </span>
          </div>
          <Badge className="bg-brand-light/10 text-brand-light border-brand-light/20 font-semibold">
            Confidencial
          </Badge>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Hero Summary */}
        <div className="mb-16">
          <p className="text-brand-light/50 text-sm font-semibold uppercase tracking-widest mb-3">
            Valoración Pre-Revenue Actualizada
          </p>
          <h1 className="brand-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
            Tu Iglesia App
          </h1>
          <p className="text-white/40 text-lg max-w-2xl mb-10">
            Reporte indicativo de valoración. Plataforma SaaS para gestión de congregaciones cristianas en el mercado hispano.
          </p>

          {/* Key Metrics Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard
              icon={<DollarSign className="w-5 h-5" />}
              label="Valoración Recomendada"
              value={valuationSummary.recommended}
              sub="Pre-money"
              accent
            />
            <MetricCard
              icon={<TrendingUp className="w-5 h-5" />}
              label="Rango Interno"
              value={valuationSummary.range}
              sub="Base razonable"
            />
            <MetricCard
              icon={<BarChart3 className="w-5 h-5" />}
              label="Múltiplo de Revaluación"
              value={valuationSummary.revaluationMultiple}
              sub={`vs. ${valuationSummary.previous} anterior`}
            />
            <MetricCard
              icon={<Target className="w-5 h-5" />}
              label="Upside Condicionado"
              value={valuationSummary.upside}
              sub="Con MRR estable"
            />
          </div>
        </div>

        {/* Tabs Navigation */}
        <Tabs defaultValue="situacion" className="space-y-8">
          <TabsList className="bg-white/[0.04] border border-brand-light/10 rounded-2xl p-1.5 flex-wrap h-auto gap-1">
            <TabsTrigger value="situacion" className="rounded-xl data-[state=active]:bg-brand-700 data-[state=active]:text-white text-white/50 px-4 py-2 text-sm font-medium">
              Situación Actual
            </TabsTrigger>
            <TabsTrigger value="mercado" className="rounded-xl data-[state=active]:bg-brand-700 data-[state=active]:text-white text-white/50 px-4 py-2 text-sm font-medium">
              Mercado
            </TabsTrigger>
            <TabsTrigger value="financiero" className="rounded-xl data-[state=active]:bg-brand-700 data-[state=active]:text-white text-white/50 px-4 py-2 text-sm font-medium">
              Proyecciones
            </TabsTrigger>
            <TabsTrigger value="valoracion" className="rounded-xl data-[state=active]:bg-brand-700 data-[state=active]:text-white text-white/50 px-4 py-2 text-sm font-medium">
              Valoración
            </TabsTrigger>
            <TabsTrigger value="captable" className="rounded-xl data-[state=active]:bg-brand-700 data-[state=active]:text-white text-white/50 px-4 py-2 text-sm font-medium">
              Cap Table
            </TabsTrigger>
            <TabsTrigger value="riesgos" className="rounded-xl data-[state=active]:bg-brand-700 data-[state=active]:text-white text-white/50 px-4 py-2 text-sm font-medium">
              Riesgos
            </TabsTrigger>
            <TabsTrigger value="hitos" className="rounded-xl data-[state=active]:bg-brand-700 data-[state=active]:text-white text-white/50 px-4 py-2 text-sm font-medium">
              Hitos
            </TabsTrigger>
          </TabsList>

          {/* SITUACIÓN ACTUAL */}
          <TabsContent value="situacion" className="space-y-8">
            <SectionTitle icon={<Briefcase />} title="Resumen de Situación Actual" />
            <div className="grid md:grid-cols-2 gap-4">
              {currentSituation.map((item, i) => (
                <div key={i} className="p-5 rounded-2xl bg-white/[0.03] border border-brand-light/8 flex justify-between items-start gap-4">
                  <span className="text-white/40 text-sm">{item.label}</span>
                  <span className="text-white font-semibold text-sm text-right">{item.value}</span>
                </div>
              ))}
            </div>

            <SectionTitle icon={<Layers />} title="Producto y Módulos" />
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {productModules.map((mod, i) => (
                <div key={i} className="p-4 rounded-xl bg-white/[0.03] border border-brand-light/6 hover:border-brand-light/15 transition-colors">
                  <h4 className="text-white font-semibold text-sm mb-1">{mod.module}</h4>
                  <p className="text-white/35 text-xs">{mod.description}</p>
                </div>
              ))}
            </div>

            <SectionTitle icon={<Users />} title="Equipo" />
            <div className="grid md:grid-cols-3 gap-4">
              {teamInfo.founders.map((f, i) => (
                <Card key={i} className="p-5 rounded-2xl bg-white/[0.03] border-brand-light/8">
                  <p className="text-white font-bold text-sm">{f.name}</p>
                  <p className="text-brand-light/60 text-xs">{f.role}</p>
                </Card>
              ))}
              <Card className="p-5 rounded-2xl bg-white/[0.03] border-brand-light/8">
                <p className="text-white font-bold text-sm">{teamInfo.cto.name}</p>
                <p className="text-brand-light/60 text-xs">{teamInfo.cto.role}</p>
              </Card>
            </div>
            <div className="flex gap-6 text-sm text-white/40">
              <span>Equipo operativo: <strong className="text-white/70">{teamInfo.teamSize} personas</strong></span>
              <span>Holding: <strong className="text-white/70">{teamInfo.holding}</strong></span>
            </div>

            <SectionTitle icon={<CheckCircle2 />} title="Tracción Pre-Revenue" />
            <div className="space-y-3">
              {tractionSignals.map((signal, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-brand-light/5">
                  <ChevronRight className="w-4 h-4 text-brand-light flex-shrink-0" />
                  <span className="text-white/55 text-sm">{signal}</span>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* MERCADO */}
          <TabsContent value="mercado" className="space-y-8">
            <SectionTitle icon={<Globe />} title="Mercado Objetivo" />
            <div className="grid md:grid-cols-3 gap-6">
              <MarketCard label="TAM" value={marketData.tam.value} desc={marketData.tam.desc} />
              <MarketCard label="SAM" value={marketData.sam.value} desc={marketData.sam.desc} />
              <MarketCard label="SOM (3 años)" value={marketData.som.value} desc={marketData.som.desc} />
            </div>

            <div className="p-6 rounded-2xl bg-white/[0.03] border border-brand-light/8">
              <h4 className="text-white font-bold text-sm mb-2">Cliente Ideal</h4>
              <p className="text-white/50 text-sm">{marketData.idealClient}</p>
            </div>

            <div className="p-6 rounded-2xl bg-white/[0.03] border border-brand-light/8">
              <h4 className="text-white font-bold text-sm mb-4">Expansión Geográfica</h4>
              <div className="flex gap-3">
                {marketData.expansion.map((e, i) => (
                  <Badge key={i} className="bg-brand-light/10 text-brand-light border-brand-light/20 text-xs">
                    {e}
                  </Badge>
                ))}
              </div>
            </div>

            <SectionTitle icon={<BarChart3 />} title="Contexto de Mercado Global" />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-brand-light/10">
                    <th className="text-left py-3 px-4 text-white/40 font-medium">Fuente</th>
                    <th className="text-left py-3 px-4 text-white/40 font-medium">Datos</th>
                    <th className="text-left py-3 px-4 text-white/40 font-medium">CAGR</th>
                  </tr>
                </thead>
                <tbody>
                  {marketData.globalMarket.map((m, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="py-3 px-4 text-white/70 font-medium">{m.source}</td>
                      <td className="py-3 px-4 text-white/50">
                        {m.value2024 ? `${m.value2024} → ${m.value2030}` : m.increase}
                      </td>
                      <td className="py-3 px-4">
                        <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-xs">{m.cagr}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* PROYECCIONES */}
          <TabsContent value="financiero" className="space-y-8">
            <SectionTitle icon={<DollarSign />} title="Modelo de Ingresos" />
            <div className="grid md:grid-cols-2 gap-4">
              {revenueModel.map((r, i) => (
                <div key={i} className="p-6 rounded-2xl bg-white/[0.03] border border-brand-light/8">
                  <h4 className="text-brand-light font-bold text-sm mb-1">{r.source}</h4>
                  <p className="text-white text-2xl font-bold mb-1">{r.perUser}</p>
                  <p className="text-white/35 text-xs">{r.description}</p>
                </div>
              ))}
            </div>

            <SectionTitle icon={<Target />} title="Piloto CCN" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MiniMetric label="Miembros" value={projections.ccnPilot.members} />
              <MiniMetric label="Congregaciones" value={projections.ccnPilot.congregations} />
              <MiniMetric label="MRR Potencial" value={projections.ccnPilot.mrrPotential} accent />
              <MiniMetric label="ARR Potencial" value={projections.ccnPilot.arrPotential} accent />
            </div>

            <SectionTitle icon={<TrendingUp />} title="Proyecciones de Crecimiento" />
            <div className="grid md:grid-cols-2 gap-6">
              <ProjectionCard
                title="Año 1"
                data={projections.year1}
              />
              <ProjectionCard
                title="Año 3"
                data={projections.year3}
              />
            </div>

            <div className="p-6 rounded-2xl bg-white/[0.03] border border-brand-light/8">
              <h4 className="text-white font-bold text-sm mb-3">Break-even Operativo</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-white/40 text-xs mb-1">ARPU completo</p>
                  <p className="text-white font-bold">{projections.breakeven.fullArpu}</p>
                </div>
                <div>
                  <p className="text-white/40 text-xs mb-1">Escenario de conversión</p>
                  <p className="text-white font-bold">{projections.breakeven.conversionScenario}</p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* VALORACIÓN */}
          <TabsContent value="valoracion" className="space-y-8">
            <SectionTitle icon={<BarChart3 />} title="Metodologías de Valoración" />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-brand-light/10">
                    <th className="text-left py-3 px-4 text-white/40 font-medium">Método</th>
                    <th className="text-left py-3 px-4 text-white/40 font-medium">Rango</th>
                    <th className="text-left py-3 px-4 text-white/40 font-medium">Uso</th>
                  </tr>
                </thead>
                <tbody>
                  {valuationMethods.map((v, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="py-3 px-4 text-white/70 font-medium">{v.method}</td>
                      <td className="py-3 px-4 text-white font-bold">{v.range}</td>
                      <td className="py-3 px-4 text-white/40">{v.usage}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <SectionTitle icon={<Layers />} title="Berkus Adaptado — Desglose" />
            <div className="grid md:grid-cols-3 gap-3">
              {berkusBreakdown.map((b, i) => (
                <div key={i} className="p-4 rounded-xl bg-white/[0.03] border border-brand-light/6 flex justify-between items-center">
                  <span className="text-white/50 text-sm">{b.factor}</span>
                  <span className="text-white font-bold text-sm">{b.value}</span>
                </div>
              ))}
            </div>

            <SectionTitle icon={<Target />} title="Escenarios de Valoración" />
            <div className="grid md:grid-cols-3 gap-4">
              {valuationScenarios.map((s, i) => (
                <Card key={i} className={`p-6 rounded-2xl border ${
                  i === 0 ? 'bg-brand-700/20 border-brand-light/20' : 'bg-white/[0.03] border-brand-light/8'
                }`}>
                  <Badge className={`mb-3 text-xs ${
                    i === 0 ? 'bg-brand-light/20 text-brand-light border-brand-light/30' : 'bg-white/10 text-white/60 border-white/15'
                  }`}>
                    {s.scenario}
                  </Badge>
                  <p className="text-white text-2xl font-bold mb-2">{s.valuation}</p>
                  <p className="text-white/35 text-xs">{s.conditions}</p>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* CAP TABLE */}
          <TabsContent value="captable" className="space-y-8">
            <SectionTitle icon={<Users />} title="Tabla de Capitalización" />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-brand-light/10">
                    <th className="text-left py-3 px-4 text-white/40 font-medium">Accionista</th>
                    <th className="text-left py-3 px-4 text-white/40 font-medium">Participación</th>
                    <th className="text-left py-3 px-4 text-white/40 font-medium">Valor Implícito</th>
                  </tr>
                </thead>
                <tbody>
                  {capTable.map((c, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="py-3 px-4 text-white/70 font-medium">{c.stakeholder}</td>
                      <td className="py-3 px-4">
                        <Badge className="bg-brand-light/10 text-brand-light border-brand-light/20 text-xs">{c.percentage}</Badge>
                      </td>
                      <td className="py-3 px-4 text-white font-bold">{c.implicitValue}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-brand-light/15">
                    <td className="py-3 px-4 text-white font-bold">Total</td>
                    <td className="py-3 px-4">
                      <Badge className="bg-brand-light/15 text-brand-light border-brand-light/25 text-xs font-bold">100%</Badge>
                    </td>
                    <td className="py-3 px-4 text-brand-light font-bold">USD 3,500,000</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="p-6 rounded-2xl bg-white/[0.03] border border-brand-light/8">
              <h4 className="text-white font-bold text-sm mb-2">Múltiplo sobre Inversión</h4>
              <p className="text-white/40 text-sm">
                Valoración / Inversión Acumulada = {valuationSummary.recommended} / USD 865K = <strong className="text-brand-light">{valuationSummary.multipleOnInvestment}</strong>
              </p>
            </div>
          </TabsContent>

          {/* RIESGOS */}
          <TabsContent value="riesgos" className="space-y-8">
            <SectionTitle icon={<AlertTriangle />} title="Matriz de Riesgos" />
            <div className="space-y-3">
              {riskMatrix.map((r, i) => (
                <div key={i} className="p-5 rounded-2xl bg-white/[0.03] border border-brand-light/8">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-white font-bold text-sm">{r.risk}</h4>
                    <Badge className={`text-xs ${
                      r.impact === "Negativo"
                        ? "bg-red-500/10 text-red-400 border-red-500/20"
                        : "bg-yellow-500/10 text-yellow-400 border-yellow-500/20"
                    }`}>
                      {r.impact}
                    </Badge>
                  </div>
                  <p className="text-white/40 text-sm">{r.mitigation}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* HITOS */}
          <TabsContent value="hitos" className="space-y-8">
            <SectionTitle icon={<Clock />} title="Próximos Hitos" />
            <div className="space-y-6">
              {milestones.map((m, i) => (
                <div key={i} className="relative pl-8">
                  <div className="absolute left-0 top-1 w-4 h-4 rounded-full bg-brand-light/20 border-2 border-brand-light/40" />
                  {i < milestones.length - 1 && (
                    <div className="absolute left-[7px] top-5 w-0.5 h-full bg-brand-light/10" />
                  )}
                  <div className="p-5 rounded-2xl bg-white/[0.03] border border-brand-light/8">
                    <Badge className="bg-brand-light/10 text-brand-light border-brand-light/20 text-xs font-bold mb-3">
                      {m.timeline}
                    </Badge>
                    <div className="space-y-2">
                      {m.actions.map((a, j) => (
                        <div key={j} className="flex items-center gap-2">
                          <ChevronRight className="w-3 h-3 text-brand-light/50 flex-shrink-0" />
                          <span className="text-white/55 text-sm">{a}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Certifications & Powered By */}
        <div className="mt-16 grid md:grid-cols-2 gap-4">
          <a
            href="https://www.alpha-sense.com"
            target="_blank"
            rel="noopener noreferrer"
            className="group p-8 rounded-2xl bg-white/[0.03] border border-brand-light/10 hover:border-brand-light/25 transition-all duration-300 block"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Shield className="w-4 h-4 text-amber-400" />
              </div>
              <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-xs">
                En proceso
              </Badge>
            </div>
            <p className="text-white/35 text-sm mb-5">
              Certificación en proceso con
            </p>
            <div className="mb-5">
              <span className="text-white text-4xl md:text-5xl font-bold tracking-tight group-hover:text-brand-light transition-colors duration-300" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
                AlphaSense
              </span>
            </div>
            <p className="text-white/30 text-sm leading-relaxed">
              Validación y compliance del reporte de valoración a través de la plataforma líder en inteligencia de mercado.
            </p>
            <span className="inline-flex items-center gap-1.5 text-brand-light/50 text-xs font-medium mt-4 group-hover:text-brand-light transition-colors">
              alpha-sense.com
              <ExternalLink className="w-3 h-3" />
            </span>
          </a>

          <a
            href="https://atypica.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="group p-8 rounded-2xl bg-white/[0.03] border border-brand-light/10 hover:border-brand-light/25 transition-all duration-300 block"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 rounded-lg bg-brand-light/10 flex items-center justify-center">
                <BarChart3 className="w-4 h-4 text-brand-light" />
              </div>
              <Badge className="bg-brand-light/10 text-brand-light border-brand-light/20 text-xs">
                Powered by
              </Badge>
            </div>
            <p className="text-white/35 text-sm mb-5">
              Estudio de valoración ejecutado bajo el motor de
            </p>
            <div className="mb-5">
              <span className="text-white text-4xl md:text-5xl font-bold tracking-tight group-hover:text-brand-light transition-colors duration-300" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
                atypica<span className="text-brand-light">.ai</span>
              </span>
            </div>
            <p className="text-white/30 text-sm leading-relaxed">
              Motor de análisis financiero e inteligencia artificial para la presentación del reporte de valoración.
            </p>
            <span className="inline-flex items-center gap-1.5 text-brand-light/50 text-xs font-medium mt-4 group-hover:text-brand-light transition-colors">
              atypica.ai
              <ExternalLink className="w-3 h-3" />
            </span>
          </a>
        </div>

        {/* Disclaimer */}
        <div className="mt-6 p-6 rounded-2xl bg-white/[0.02] border border-white/5">
          <p className="text-white/20 text-xs leading-relaxed">
            <strong className="text-white/30">Aviso:</strong> Este es un reporte indicativo de valoración, no constituye una opinión auditada. Las proyecciones financieras son escenarios estimados, no garantías. La información contenida es confidencial y de uso exclusivo para inversores autorizados de Tu Iglesia App.
          </p>
        </div>
      </div>
    </div>
  );
}

// Sub-components

const SectionTitle = ({ icon, title }) => (
  <div className="flex items-center gap-3 pt-4">
    <div className="w-8 h-8 rounded-lg bg-brand-light/10 flex items-center justify-center text-brand-light">
      {icon}
    </div>
    <h3 className="brand-heading text-xl font-bold text-white">{title}</h3>
  </div>
);

const MetricCard = ({ icon, label, value, sub, accent }) => (
  <Card className={`p-5 rounded-2xl border ${
    accent ? 'bg-brand-700/15 border-brand-light/20' : 'bg-white/[0.03] border-brand-light/8'
  }`}>
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-3 ${
      accent ? 'bg-brand-light/20 text-brand-light' : 'bg-white/10 text-white/50'
    }`}>
      {icon}
    </div>
    <p className="text-white/40 text-xs mb-1">{label}</p>
    <p className={`text-xl font-bold ${accent ? 'text-brand-light' : 'text-white'}`}>{value}</p>
    {sub && <p className="text-white/25 text-xs mt-1">{sub}</p>}
  </Card>
);

const MarketCard = ({ label, value, desc }) => (
  <Card className="p-6 rounded-2xl bg-white/[0.03] border-brand-light/8 text-center">
    <Badge className="bg-brand-light/10 text-brand-light border-brand-light/20 text-xs mb-3">{label}</Badge>
    <p className="text-3xl font-bold text-white mb-1">{value}</p>
    <p className="text-white/35 text-xs">{desc}</p>
  </Card>
);

const MiniMetric = ({ label, value, accent }) => (
  <div className="p-4 rounded-xl bg-white/[0.03] border border-brand-light/6">
    <p className="text-white/35 text-xs mb-1">{label}</p>
    <p className={`font-bold text-sm ${accent ? 'text-brand-light' : 'text-white'}`}>{value}</p>
  </div>
);

const ProjectionCard = ({ title, data }) => (
  <Card className="p-6 rounded-2xl bg-white/[0.03] border-brand-light/8">
    <Badge className="bg-brand-light/10 text-brand-light border-brand-light/20 text-xs font-bold mb-4">{title}</Badge>
    <div className="space-y-3">
      <div className="flex justify-between">
        <span className="text-white/40 text-sm">Congregaciones</span>
        <span className="text-white font-bold text-sm">{data.congregations}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-white/40 text-sm">Usuarios</span>
        <span className="text-white font-bold text-sm">{data.users}</span>
      </div>
      <Separator className="bg-white/5" />
      <div className="flex justify-between">
        <span className="text-white/40 text-sm">MRR</span>
        <span className="text-brand-light font-bold text-sm">{data.mrr}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-white/40 text-sm">ARR</span>
        <span className="text-brand-light font-bold text-sm">{data.arr}</span>
      </div>
    </div>
  </Card>
);
